import React, { useState, useEffect } from 'react';
import { getPerformanceResponses } from '../../../../services/performanceService';
import { getScheduledNotificationForObservationPeriod } from '../../../../services/notificationsService';
import { Box, Card, CardContent, Typography, Button, Tooltip, Chip } from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import './ObservationPeriods.css';

const ObservationPeriods = ({ userId, onSelectPeriod }) => {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
    const { t } = useTranslation();
    const timeZone = 'Europe/Berlin';

    useEffect(() => {
        const fetchObservationPeriods = async () => {
            // Don't make API calls if userId is not provided or invalid
            if (!userId || userId === 'undefined') {
                console.warn('ObservationPeriod: userId is not defined, skipping data fetch');
                setPeriods([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Fetch scheduled notifications first
                const scheduledNotifications = await getScheduledNotificationForObservationPeriod(userId);
                
                if (!scheduledNotifications || !scheduledNotifications.dateRanges || scheduledNotifications.dateRanges.length === 0) {
                    console.log('No scheduled notification date ranges found.');
                    setPeriods([]);
                    setLoading(false);
                    return;
                }

                // Try to fetch performance data, but handle 404 gracefully
                let performanceData = { data: [] };
                try {
                    const response = await getPerformanceResponses(userId);
                    performanceData = response;
                } catch (error) {
                    if (error.response && error.response.status === 404) {
                        console.log('No performance responses found yet - this is normal for new users');
                        performanceData = { data: [] };
                    } else {
                        throw error;
                    }
                }

                // Validate performance data structure
                if (!performanceData.data) {
                    performanceData.data = [];
                }

                const calculatedPeriods = scheduledNotifications.dateRanges.map((range, index) => {
                    const startDate = moment(range.startDate).tz(timeZone).toDate();
                    const endDate = moment(range.endDate).tz(timeZone).toDate();

                    // Directly use the notification times
                    const notificationTimes = range.notifications.map(notif => moment(notif.notificationTime).tz(timeZone));
                    const expectedQuestions = range.notifications.length;

                    // Filter responses to match scheduled slots
                    const responsesInPeriod = performanceData.data.filter((response) => {
                        const responseDate = moment(response.timestamp).tz(timeZone);
                        return notificationTimes.some(slot => responseDate.isSame(slot, 'hour'));
                    });

                    const completedQuestions = responsesInPeriod.length;

                    return {
                        start: startDate,
                        end: endDate,
                        startDate: startDate, // Include both formats for compatibility
                        endDate: endDate,
                        completedQuestions,
                        expectedQuestions,
                        notifications: notificationTimes, // Pass the moment objects for notifications
                        rawNotifications: range.notifications, // Also pass raw notifications if needed
                        periodIndex: index + 1,
                        status: range.status, // Pass the status (e.g., 'restarted')
                        isArchived: range.isArchived
                    };
                });

                setPeriods(calculatedPeriods);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching observation periods:', error);
                setPeriods([]);
                setLoading(false);
            }
        };

        fetchObservationPeriods();
    }, [userId, timeZone]);

    const handlePeriodClick = (index) => {
        console.log('Period clicked:', periods[index]); // Debug log
        onSelectPeriod(periods[index]);
    };

    if (loading) {
        return <Typography>{t('overview.loading')}</Typography>;
    }

    if (periods.length === 0) {
        return (
            <Box sx={{ width: '100%', textAlign: 'center', p: 2 }}>
                <Typography variant="body1" color="textSecondary">
                    {t('overview.noObservationPeriods', 'No observation periods scheduled yet.')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Card
                variant="outlined"
                className="observation-card"
                sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: 4,
                        backgroundColor: '#f0f8ff',
                    },
                    cursor: 'pointer'
                }}
                onClick={() => handlePeriodClick(currentPeriodIndex)}
            >
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                                {t('overview.observationPeriod', {
                                    index: currentPeriodIndex + 1,
                                    start: periods[currentPeriodIndex].start.toLocaleDateString(),
                                    end: periods[currentPeriodIndex].end.toLocaleDateString(),
                                })}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                {t('overview.completedQuestions', {
                                    completed: periods[currentPeriodIndex].completedQuestions,
                                    expected: periods[currentPeriodIndex].expectedQuestions
                                })}
                            </Typography>
                        </Box>
                        
                        {/* Status indicator */}
                        {periods[currentPeriodIndex].status === 'restarted' ? (
                            <Tooltip title={t('overview.periodEndedEarly')} arrow>
                                <Chip
                                    icon={<Warning />}
                                    label={t('overview.endedEarly')}
                                    color="warning"
                                    size="small"
                                    sx={{ ml: 2 }}
                                />
                            </Tooltip>
                        ) : periods[currentPeriodIndex].completedQuestions === periods[currentPeriodIndex].expectedQuestions ? (
                            <Tooltip title={t('overview.periodCompleted')} arrow>
                                <Chip
                                    icon={<CheckCircle />}
                                    label={t('overview.completed')}
                                    color="success"
                                    size="small"
                                    sx={{ ml: 2 }}
                                />
                            </Tooltip>
                        ) : null}
                    </Box>
                </CardContent>
            </Card>

            {periods.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setCurrentPeriodIndex(prev => Math.max(prev - 1, 0))}
                        disabled={currentPeriodIndex === 0}
                    >
                        {t('overview.previous')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setCurrentPeriodIndex(prev => Math.min(prev + 1, periods.length - 1))}
                        disabled={currentPeriodIndex === periods.length - 1}
                    >
                        {t('overview.next')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ObservationPeriods;